import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardContent, CardHeader, CardTitle, CardDescription } from '@hiring-intelligence/ui';
import { LayoutGrid, List as ListIcon } from 'lucide-react';
import { sourcingApi, SourcedCandidate } from '@/services/api';
import { CandidateDetailModal } from './CandidateDetailModal';
import { CandidatePipelineCard } from './CandidatePipelineCard';
import { CandidateKanbanBoard } from './CandidateKanbanBoard';
import { AddCandidateSection } from './AddCandidateSection';
import { useCandidateVaultUpdates } from '@/services/socket';

interface CandidateProcessListProps {
	hiringRequestId: string;
}

export function CandidateProcessList({ hiringRequestId }: CandidateProcessListProps) {
	const queryClient = useQueryClient();
	const [selectedCandidate, setSelectedCandidate] = useState<SourcedCandidate | null>(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [view, setView] = useState<'kanban' | 'list'>('kanban');

	// Get saved candidates
	const { data: savedCandidates, refetch: refetchCandidates } = useQuery({
		queryKey: ['sourced-candidates', hiringRequestId],
		queryFn: () => sourcingApi.getCandidates(hiringRequestId)
	});

	// Recommended candidates first; the rest keep the API order (stable sort).
	const sortedCandidates: SourcedCandidate[] = [...(savedCandidates?.data || [])].sort(
		(a, b) => Number(b.recommended) - Number(a.recommended)
	);

	const handleRefresh = () => {
		refetchCandidates();
		queryClient.invalidateQueries({ queryKey: ['hiring-request', hiringRequestId] });
		queryClient.invalidateQueries({ queryKey: ['hiring-requests'] });
	};

	// Listen globally for vault updates and refresh this hiring request pipeline list.
	useCandidateVaultUpdates(null, () => {
		handleRefresh();
	});

	return (
		<div className="space-y-6">
			{/* Add Candidate Section */}
			<AddCandidateSection
				hiringRequestId={hiringRequestId}
				onCandidateAdded={handleRefresh}
			/>

			{/* Saved Candidates */}
			<Card>
				<CardHeader className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
					<div>
						<CardTitle>Saved Candidates ({savedCandidates?.data?.length || 0})</CardTitle>
						<CardDescription>Candidates in your pipeline for this role</CardDescription>
					</div>
					<div className="inline-flex shrink-0 rounded-md border p-0.5">
						<Button variant={view === 'kanban' ? 'default' : 'ghost'} size="sm" onClick={() => setView('kanban')} className="gap-1.5">
							<LayoutGrid className="h-4 w-4" /> Kanban
						</Button>
						<Button variant={view === 'list' ? 'default' : 'ghost'} size="sm" onClick={() => setView('list')} className="gap-1.5">
							<ListIcon className="h-4 w-4" /> List
						</Button>
					</div>
				</CardHeader>
				<CardContent>
					{savedCandidates?.data?.length === 0 ? (
						<p className="text-muted-foreground text-center py-4">
							No candidates saved yet. Search and save candidates in the Sourcing tab.
						</p>
					) : view === 'kanban' ? (
						<CandidateKanbanBoard
							candidates={sortedCandidates}
							hiringRequestId={hiringRequestId}
							onOpenDetail={(c) => {
								setSelectedCandidate(c);
								setIsModalOpen(true);
							}}
							onRefresh={handleRefresh}
						/>
					) : (
						<div className="space-y-3">
							{sortedCandidates.map((candidate: SourcedCandidate) => (
								<CandidatePipelineCard
									key={candidate.id}
									candidate={candidate}
									hiringRequestId={hiringRequestId}
									onOpenDetail={(c) => {
										setSelectedCandidate(c);
										setIsModalOpen(true);
									}}
									onRefresh={handleRefresh}
								/>
							))}
						</div>
					)}
				</CardContent>
			</Card>

			{/* Candidate Detail Modal */}
			<CandidateDetailModal
				candidate={selectedCandidate}
				open={isModalOpen}
				onOpenChange={setIsModalOpen}
			/>
		</div>
	);
}